/* eslint-disable no-param-reassign */
import _ from 'underscore';
import OdataAdapter from '@bingads-webui/js-data-odata';
import { convertPredicatesToFilters } from '@bingads-webui-campaign/report-create';
import pgrid from 'projection-grid';

const { cloneDeep } = require('lodash');

// keep polling task for 2 minutes until the task is completed
const InitialPollTimeout = 990;
const RepeatedPollTimeout = 2990;
const PollMaxRetry = 41;
const CheckStateInterval = 2990;
const CheckStateMaxRetry = 41;

const HoursToExpire = 2;

// There is a column translation between Reporting columns and grid columns
// e.g. Spend is recognized as TotalEffectiveCost in Reporting.
// odata used to do the translation for UI, so UI always uses Spend
// now reporting does not do the translation, so UI needs to do the translation
// so UI can send columns to MT that it understands
const ReportingColumnMap = {
  // global map
  Spend: 'TotalEffectiveCost',

  ViewThroughConversionsRevenue: 'ViewThroughRevenue',
  ViewThroughConversionsCPA: 'ViewThroughCPA',
  ViewThroughConversionsReturnOnAdSpend: 'ViewThroughReturnOnAdSpend',
  ViewThroughConversionsRate: 'ViewThroughConversionRate',

  // per report-type map
  AgeAndGender: {
    EstimatedImpressions: 'Impressions',
    EstimatedImpressionRate: 'ImpressionRate',
    EstimatedClicks: 'Clicks',
    EstimatedClickRate: 'ClickRate',
    EstimatedConversions: 'Conversions',
    EstimatedConversionRate: 'ConversionRate',
    EstimatedAllConversions: 'AllConversions',
    EstimatedAllConversionRate: 'AllConversionRate',
    EstimatedCTR: 'CTR',
  },

  SearchTerms: {
    CampaignType: 'CampaignCampaignType',
  },
};

// vice versa, we need to translate data back to columns that UI understands
// before sending the data to pgrid
const GridColumnMap = {
  // global map
  TotalEffectiveCost: 'Spend',

  ViewThroughRevenue: 'ViewThroughConversionsRevenue',
  ViewThroughCPA: 'ViewThroughConversionsCPA',
  ViewThroughReturnOnAdSpend: 'ViewThroughConversionsReturnOnAdSpend',
  ViewThroughConversionRate: 'ViewThroughConversionsRate',

  // per report-type map
  AgeAndGender: {
    Impressions: 'EstimatedImpressions',
    ImpressionRate: 'EstimatedImpressionRate',
    Clicks: 'EstimatedClicks',
    ClickRate: 'EstimatedClickRate',
    Conversions: 'EstimatedConversions',
    ConversionRate: 'EstimatedConversionRate',
    AllConversions: 'EstimatedAllConversions',
    AllConversionRate: 'EstimatedAllConversionRate',
    CTR: 'EstimatedCTR',
  },

  SearchTerms: {
    CampaignCampaignType: 'CampaignType',
  },
};

const ReportAggregationMap = {
  TimeHourOfDay: 'HourOfDay',
  TimeDayOfWeek: 'DayOfWeek',
  // TimeDate: 'Summary', put here for completion
  TimeWeek: 'Weekly',
  TimeMonth: 'Monthly',
  TimeQuarter: 'Quarterly',
  TimeYear: 'Yearly',
};

const DefaultReportDataRequestPayload = {
  '@odata.type': '#Model.ReportDataTaskItem',
  AttachFileToEmail: false,
  CampaignReportScopes: [],
  ClientApplicationType: 'ReportingUI',
  Cron: '',
  IsDVS: false,
  MaxRowCount: 0,
  Name: 'CampaignUI VNext Dimensions Report',
  RenderType: 'Table',
  SchemaName: '_Default',
  Type: 'DimensionTabReport',
};

const taskItemsEntity = {
  class: 'TaskItems',
  endpoint: 'TaskItems',
  idAttribute: undefined,
  name: 'TaskItems',
};

const taskItemExecutionEntity = {
  class: 'TaskItemExecution',
  endpoint: 'TaskItemExecution',
  idAttribute: undefined,
  name: 'TaskItemExecution',
};

const NOT_STARTED = 0;
const RUNNING = 1;
const COMPLETED = 2;
const TIMED_OUT = 3;
const FAILED = 4;

export class DimensionsDataSource extends pgrid.dataSource.Base {
  constructor(props) {
    super('Id'); // pass in the key
    const {
      scope,
      serverContext,
      reportType,
      getSelectedScope,
      i18n,
      updateDownloadUrl,
    } = props;

    this.scope = scope;
    this.serverContext = serverContext;
    this.reportType = reportType;

    this.getSelectedScope = getSelectedScope;
    this.i18n = i18n;
    this.updateDownloadUrl = updateDownloadUrl;

    this.odataAdapter = new OdataAdapter();

    this.state = NOT_STARTED;
    this.pollRetry = 0;
    this.checkStateRetry = 0;

    // options is required by error-handlers
    this.options = {};
  }

  isTaskValid = (oldTime, newTime) => {
    // only check same day or next day as user session only lasts 20 hours
    let duration = newTime.getHours() - oldTime.getHours();
    if (duration < 0) {
      duration += 24;
    }
    return duration <= HoursToExpire;
  }

  paramChanged = param => !_.isEqual(this.oldParam.query, param.query)
    || !_.isEqual(this.oldParam.select, param.select)
    || !_.isEqual(this.oldParam.orderby, param.orderby)
    || !_.isEqual(this.oldParam.filter, param.filter);

  shouldUseExistingReport = (selectedScope, param) => this.taskId
    && this.oldTime
    && this.oldParam
    && !this.paramChanged(param)
    && _.isEqual(selectedScope, this.selectedScope)
    && this.isTaskValid(this.oldTime, new Date());

  query(param) {
    // clone param to avoid changing its value
    const paramClone = cloneDeep(param);

    // get selected account ids from dimensions page
    const selectedScope = this.getSelectedScope();

    if (this.shouldUseExistingReport(selectedScope, paramClone)) {
      // if we have valid task and still within time limit
      // directly pull this report without creating new data
      // this is to improve performace

      return new Promise((resolve, reject) => {
        resolve(this.getReportData(paramClone, reject));
      });
    }

    this.selectedScope = selectedScope;
    this.state = RUNNING;
    return new Promise((resolve, reject) => {
      this.createReport(paramClone, reject);

      const CheckState = () => {
        if (this.checkStateRetry === CheckStateMaxRetry) {
          reject();
        }
        this.checkStateRetry += 1;
        switch (this.state) {
          case COMPLETED:
            resolve(this.getReportData(paramClone, reject));
            break;
          case RUNNING:
            setTimeout(CheckState, CheckStateInterval);
            break;
          case TIMED_OUT:
          case NOT_STARTED:
          case FAILED:
          default:
            reject();
        }
      };

      // a loop to check state until it's either complete/timed out or it reaches retry limit
      CheckState();
    });
  }

  createReport(param, reject) {
    this.odataAdapter.create(
      taskItemsEntity,
      { ReportData: this.buildPayload(param) },
      this.buildOptions(`/Customers(${this.scope.values.cid})/TaskItems/SubmitReportDataTaskItem?$expand=TaskItemExecutions`)
    ).then((response) => {
      this.taskId = response.TaskItemExecutions[0].Id;
      setTimeout(this.pollTaskStatus.bind(this), InitialPollTimeout);
    }).catch((error) => {
      this.state = FAILED;
      reject(`create report failed with error ${error}`);
    });
  }

  pollTaskStatus() {
    if (this.pollRetry >= PollMaxRetry) {
      this.state = TIMED_OUT;
      return;
    }

    this.pollRetry += 1;

    this.odataAdapter.find(
      taskItemExecutionEntity,
      this.taskId,
      this.buildOptions(`/Customers(${this.scope.values.cid})/TaskItemExecutions(${this.taskId})/Default.PollReportDataTaskItemExecution`)
    ).then((response) => {
      switch (response.Status) {
        case 'PendingExecution':
        case 'InProgress':
          this.state = RUNNING;
          break;
        case 'Completed':
          this.updateDownloadUrl(response.ResultId);
          this.state = COMPLETED;
          break;
        case 'Failed':
          // eslint-disable-next-line no-fallthrough
        default:
          this.state = FAILED;
      }
      if (this.state === RUNNING) {
        // poll again after timeout
        setTimeout(this.pollTaskStatus.bind(this), RepeatedPollTimeout);
      }
    });
  }

  getReportData(param, reject) {
    if (this.state !== COMPLETED) {
      reject();
    }

    return this.odataAdapter.find(
      taskItemExecutionEntity,
      this.taskId,
      this.buildOptions(`/Customers(${this.scope.values.cid})/TaskItemExecutions(${this.taskId})/Default.ReportData()?$top=${param.take}&$skip=${param.skip}`)
    ).then((odata) => {
      // remove first row from data which is column definitions
      if (odata.value) {
        odata.value.shift();
      }

      // translate reporting columns back to grid columns for display
      // iterate each row then each cell in the row, replace key if it's found in the mapping
      // then return a new key: value pair entry to recreate the row
      odata.value = _.map(odata.value, row =>
        (_.map(Object.entries(row), ([column, value]) =>
          [this.translateColumn(GridColumnMap, column), value]).reduce((op, [column, value]) => {
          op[column] = value;
          return op;
        }, {})));

      const data = {
        items: odata.value || [],
        totalCount: odata['@odata.count'] || 0,
      };
      // only cache time and param if they're not set
      // or the param was set but changed
      // otherwise we'll infinitely push the timeout away
      if ((!this.oldTime && !this.oldParam) || this.paramChanged(param)) {
        this.oldTime = new Date();
        this.oldParam = param;
      }
      return data;
    });
  }

  buildPayload(param) {
    // generate dates
    const dateFormat = 'yyyy-MM-dd';
    const StartDate = this.i18n.formatDate(new Date(param.query.startDate), { raw: dateFormat });
    const EndDate = this.i18n.formatDate(new Date(param.query.endDate), { raw: dateFormat });

    // generate sorts
    // ignore sort column if it's not found in all selected columns
    const validSorts = _.filter(param.orderby, orderby => _.contains(param.select, orderby.key));
    const ReportSorts = _.map(validSorts, (orderby) => {
      const column = orderby.key;
      return {
        ReportColumn: this.translateColumn(ReportingColumnMap, column),
        SortDirection: orderby.direction === 1 ? 'ASCENDING' : 'DESCENDING',
      };
    });

    // generate filters
    // when there is only one filter, its format is like this
    // we need to push the only one into predicates
    // param.filter {
    //   AdGroupName: {
    //     $contains: '1'
    //   }
    // }
    // when there is more than one filter, the format is different, an array
    // we need to concat the array to predicates
    // param.filter {
    //   $and: [
    //     AdGroupName: {
    //       $contains: '1'
    //     },
    //     Clicks: {
    //       $equals: 10
    //     },
    //   ]
    // }

    let predicates = [];
    if (param.filter) {
      _.each(param.filter, (filter) => {
        if (Array.isArray(filter)) {
          predicates = predicates.concat(filter);
        } else {
          predicates.push(param.filter);
        }
      });
    }

    // transform predicates from
    // EstimatedClicks: {
    //   $eq: '1'
    // }
    // to
    // Clicks: {
    //   $eq: '1'
    // }
    let processedPredicates = _.map(predicates, (predicate) => {
      const newPrediate = {};

      _.each(Object.keys(predicate), (key) => {
        newPrediate[this.translateColumn(ReportingColumnMap, key)] = predicate[key];
      });

      return newPrediate;
    });

    // transform schema from
    // schema: {
    //   EstimatedClicks: {
    //     name: 'EstimatedClicks',
    //     originalProperty: 'EstimatedClicks',
    //     property: 'EstimatedClicks',
    //     ... other props
    //   }
    // }
    // to
    // schema: {
    //   Clicks: {
    //     name: 'Clicks',
    //     originalProperty: 'Clicks',
    //     property: 'Clicks',
    //     ... other props
    //   }
    // }

    const processedSchema = {};

    _.each(Object.keys(this.filterSchema), (key) => {
      const newKey = this.translateColumn(ReportingColumnMap, key);

      // append Clicks to new schema instead of EstimatedClicks
      processedSchema[newKey] = this.filterSchema[key];

      // change newSchame.Clicks.name from EstimatedClicks to Clicks, so are originalProperty and property
      if (processedSchema[newKey].name && typeof processedSchema[newKey].name === 'string') {
        processedSchema[newKey].name = this.translateColumn(ReportingColumnMap, processedSchema[newKey].name);
      }
      if (processedSchema[newKey].originalProperty && typeof processedSchema[newKey].originalProperty === 'string') {
        processedSchema[newKey].originalProperty = this.translateColumn(ReportingColumnMap, processedSchema[newKey].originalProperty);
      }
      if (processedSchema[newKey].property && typeof processedSchema[newKey].property === 'string') {
        processedSchema[newKey].property = this.translateColumn(ReportingColumnMap, processedSchema[newKey].property);
      }
    });

    // preprocess filter into dimension MT expected format,
    // e.g. for number enums [1,2,3], turn them into strings ['1','2','3']

    processedPredicates = _.map(processedPredicates, (predicate) => {
      const propertyName = _.keys(predicate)[0];
      const propertySchema = processedSchema[propertyName];
      const propertyValueObject = predicate[propertyName];
      const operatorUi = _.keys(propertyValueObject)[0];
      const propertyValueUi = propertyValueObject[operatorUi];

      if (propertySchema.enum) {
        propertyValueObject[operatorUi] = propertyValueUi.map(String);
      } else if (propertySchema.type === 'string') {
        propertyValueObject[operatorUi] = [propertyValueUi];
      } else if (propertySchema.type === 'integer' || propertySchema.type === 'number') {
        propertyValueObject[operatorUi] = [propertyValueUi.toString()];
      } else if (propertySchema.type === 'id') {
        propertyValueObject[operatorUi] = propertyValueUi;
      }

      return predicate;
    });

    const valueHandledByCaller = true;
    const Filters = convertPredicatesToFilters(processedPredicates, processedSchema, valueHandledByCaller);

    const payload = {
      CustomerId: this.scope.values.cid,
      Filters,
      LCID: this.serverContext.CurrentUser.LocaleID,
      ReportAggregation: _.result(ReportAggregationMap, this.reportType, 'Summary'),
      // replace grid columns to Reporting columns
      ReportColumns: _.map(param.select, column =>
        this.translateColumn(ReportingColumnMap, column)),
      ReportOptions: 'IncludeColumnHeaders,IncludeReportFooter,IncludeReportHeader',
      ReportSorts,
      ReportTime: {
        StartDate,
        EndDate,
      },
      ReportType: this.reportType,
      TimeZoneId: this.serverContext.CurrentAccount.TimeZone,
      UserId: this.scope.values.uid,
    };

    // for customer level request, send CID in CustomerScopes
    // for account level request, send AIDs in AccountIds
    // these two are mutually exclusive
    if (!this.selectedScope.AccountIds || (this.selectedScope.AccountIds.length === 0)) {
      payload.CustomerScopes = [this.scope.values.cid];
    } else {
      payload.AccountIds = this.selectedScope.AccountIds;
    }

    _.defaults(payload, DefaultReportDataRequestPayload);

    return payload;
  }

  buildOptions(url) {
    return {
      ajaxOptions: {
        host: this.appConfig.get('odata.url'),
        path: this.appConfig.get('odata.multipart_path'),
        oDataToken: this.serverContext.Token,
        sessionId: this.serverContext.Omniture.SessionId,
      },
      edm: {
        types:
        {},
      },
      entitySet: {
        odataURL: url,
        navigation: {
          source: {
            type: {
            },
          },
        },
        $withKey: () => ({
          odataURL: url,
        }),
      },
    };
  }

  translateColumn(columnMap, column) {
    // first find a global match
    const globalMatch = _.result(columnMap, column, column);

    // check if there is per report-type map
    return _.result(columnMap[this.reportType], column, globalMatch);
  }
}
